import { AxiosResponse } from "axios";
import moment, { Moment } from "moment";

import { http } from "../api-http";
import { List } from "../models/list.model";
import { Station } from "../models/station.model";
import { StationEvent } from "../models/station-event.model";
import { Message } from "../models/message.model";
import { StationGroup } from "../models/station-group.model";
import { Measure } from "../models/measure.model";
import { StationStats } from "../models/station-stats.model";
import { RequiredError } from '~/api/exceptions/required.error';

export class StationService {

    private static _instance: StationService | null = null;

    static getInstance(): StationService {
        if (!StationService._instance) {
            StationService._instance = new StationService();
        }
        return StationService._instance;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {
    }

    getUserStation(): Promise<Station[]> {
        return http.get<List<Station>>('/api/stations/by-user').then((response: AxiosResponse<List<Station>>) => {
            return response.data.data.filter((station: Station) => ['avolta-ch4', 'avolta-lorawan', 'avolta-ltem'].includes(station.type));
        })
    }

    getUserGroups(): Promise<StationGroup[]> {
        return http.get<List<StationGroup>>('/api/stationgroups?tag=avolta').then((response: AxiosResponse<List<StationGroup>>) => {
            const groups = response.data.data;
            return typeof groups === 'object' ? Object.values(groups) : groups;
        });
    }

    getStationsByGroup(groupName: string): Promise<Station[]> {
        return http.get<List<Station>>('/api/stations', { params: {group_name: groupName}})
            .then((response: AxiosResponse<List<Station>>) => {
                return response.data.data.filter((station: Station) => ['avolta-ch4', 'avolta-lorawan', 'avolta-ltem'].includes(station.type));
            });
    }

    searchStationInBounds(bbox: string): Promise<Station[]> {

        if (!bbox) {
            return Promise.reject(new Error('bbox is null of undefined'));
        }

        return http.get<List<Station>>('/api/stations', { params: { bbox } })
            .then((response: AxiosResponse<List<Station>>) => {
                return response.data.data.filter((station: Station) => ['avolta-ch4', 'avolta-lorawan', 'avolta-ltem'].includes(station.type));
            });
    }

    getStationDetails(stationId: string): Promise<Station> {
        return http.get<Station>(`/api/stations/${stationId}?aqi=elichens`).then((response: AxiosResponse<Station>) => {
            return response.data;
        })
    }


    getStationStats(stationId: string, since: Moment = null, before: Moment = null): Promise<StationStats> {
        if (!since) {
            since = moment().startOf('hour').subtract(1, 'd');
        }

        if (!before) {
            before = moment().startOf('hour');
        }

        return http.get<StationStats>(`/api/stations/${stationId}/stats`, { params: { aqi: 'elichens', since: since.unix(), before: before.unix() } }).then((response: AxiosResponse<StationStats>) => {
            return response.data;
        })
    }

    getAverage(stationId: string, step: number = 3600, since: Moment = null, before: Moment = null, limit: number = 24): Promise<Measure[]> {

        if (!since) {
            since = moment().startOf('hour').subtract(1, 'd');
        }

        if (!before) {
            before = moment().startOf('hour');
        }
        return http.get<List<Measure>>(
            `/api/stations/${stationId}/averages?aqi=elichens`,
            {
                params: {
                    step,
                    since: since.unix(),
                    before: before.unix(),
                    limit
                }
            }
        ).then((response: AxiosResponse<List<Measure>>) => {
            return response.data.data;
        })
    }

    getMeasures(stationId: string, since: Moment, before: Moment = null, hourly: boolean = false) {
        if (!since) {
            since = moment().startOf('hour').subtract(1, 'd');
        }

        if (!before) {
            before = moment();
        }

        const url = hourly ?  `/api/stations/${stationId}/hourly?aqi=elichens`:  `/api/stations/${stationId}/measures?aqi=elichens`;

        return http.get<List<Measure>>(
            url,
            {
                params: {
                    since: since.unix(),
                    before: before.unix(),
                }
            }
        ).then((response: AxiosResponse<List<Measure>>) => {
            return response.data.data;
        })
    }

    getLastFewMeasures(stationId: string, limit = 1) {
        return http.get<List<Measure>>(
            `/api/stations/${stationId}/measures`,
            {
                params: {
                    limit,
                }
            }
        ).then((response: AxiosResponse<List<Measure>>) => {
            return response.data.data;
        })
    }

    getLastFewEvents(stationId: string, limit = 10) {
        return http.get<List<StationEvent>>(`/api/stations/${stationId}/events`, {
            params: {
                limit,
            }
        })
            .then((response: AxiosResponse<List<StationEvent>>) => {
                return response.data.data;
            });
    }

    getStationEvents(stationId: string, since: moment.Moment, before: moment.Moment = null): Promise<StationEvent[]> {

        if(!stationId) {
            throw new Error('station id is required') ;
        }

        if (!since) {
            since = moment().startOf('hour').subtract(1, 'd');
        }

        if (!before) {
            before = moment();
        }


        return http.get<List<StationEvent>>(`/api/stations/${stationId}/events`, {
            params: {
                since: since.unix(),
                before: before.unix(),
            }
        })
            .then((response: AxiosResponse<List<StationEvent>>) => {
                return response.data.data;
            });
    }

    getStationMessages(stationId: string, since: moment.Moment, before: moment.Moment): Promise<Message[]> {

        if(!stationId) {
            throw new Error('station id is required') ;
        }

        return http.get<List<Message>>(`/api/stations/${stationId}/messages`, {
            params: {
                since: since.unix(),
                before: before.unix(),
            }
        })
            .then((response: AxiosResponse<List<Message>>) => {
                return response.data.data;
            });
    }

    pairStation(pairingToken: string): Promise<Station> {
        if (pairingToken.length === 9) {
            pairingToken = pairingToken.match(/.{3}/g).join('-');
        }

        return http.post<Station>(`/api/userinfo/pairing-station`, { 'pairing-token': pairingToken }).then((response: AxiosResponse<Station>) => {
            return response.data;
        })
    }

    updateStation(stationId: string, station: Partial<Station>): Promise<Station> {

        if(!stationId) {
            throw new RequiredError(this.constructor.name, `Station id is required to update station`);
        }

        const allowed = ['metadata', 'location'];
        const toUpdate = Object.fromEntries(
            Object.entries(station || {}).filter(([key]: [string, unknown]) => allowed.includes(key))
        )
        return http.put(`/api/stations/${stationId}`, toUpdate).then((response: AxiosResponse<Station>) => {
            return response.data;
        });
    }
}